import React, { useCallback, useEffect, useState } from 'react';
import { Table, Typography, Spin, Tag, Card, Button } from 'antd';
import callApi from '../../Api/config';
import { DOCUMITRA_PARTNERS, PARTNER_ORDERS } from '../../constants/EntityName';
import Accordian from '../../components/Accordian/Accordian';
import { InvertFillButton } from '../../components/InvertFillButton';
import { title } from 'process';
import { toastError, toastSuccess } from '../../service/Toast';

interface AgentPaymentLedgerDetailsProps {
  partnerId: string;
  onOpenOrder?: (order: any) => void;
  registerRefresh?: (refresh: () => void) => void;
  /** When true, skip Agent Information (already on partner profile) and wrap in one dropdown */
  hideAgentInfo?: boolean;
}

const AgentPaymentLedgerDetails: React.FC<AgentPaymentLedgerDetailsProps> = ({
  partnerId,
  onOpenOrder,
  registerRefresh,
  hideAgentInfo = false,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [agentDetails, setAgentDetails] = useState<any>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [sendingInvoice, setSendingInvoice] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch Agent Details
      const agentRes: any = await callApi(`${DOCUMITRA_PARTNERS}/${partnerId}`, 'GET');
      if (agentRes?.status) {
        setAgentDetails(agentRes?.data?.data || agentRes?.data);
      } else {
        setAgentDetails(agentRes?.data);
      }

      // Fetch Agent Orders
      const ordersRes: any = await callApi(
        `${PARTNER_ORDERS}?partnerId=${partnerId}&page=1&limit=500`,
        'GET'
      );
      if (ordersRes?.status) {
        const rawData = ordersRes?.data?.data || ordersRes?.data;
        const items = Array.isArray(rawData) ? rawData : rawData?.results || rawData?.items || [];
        setOrders(items);
      }

      // Fetch Agent Payments
      const paymentsRes: any = await callApi(
        `/partner-portal/admin/payments?partnerId=${partnerId}&page=1&limit=500`,
        'GET'
      );
      if (paymentsRes?.status) {
        const rawData = paymentsRes?.data?.data || paymentsRes?.data;
        const items = Array.isArray(rawData) ? rawData : rawData?.results || rawData?.items || [];
        setPayments(items);
      }
    } catch (error) {
      console.error('Error fetching agent ledger details:', error);
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    if (!partnerId) return;
    fetchData();
  }, [partnerId, fetchData]);

  // Lets the order tab refresh this ledger after approving or rejecting a payment
  useEffect(() => {
    registerRefresh?.(fetchData);
  }, [registerRefresh, fetchData]);

  const handleSendInvoice = async () => {
    if (selectedRowKeys.length === 0) return;
    setSendingInvoice(true);
    try {
      const res: any = await callApi('/partner-portal/groups/send-invoice', 'POST', {
        partnerId,
        groupIds: selectedRowKeys,
      });

      if (res?.status) {
        toastSuccess('Invoice sent successfully to the partner.');
        setSelectedRowKeys([]);
      } else {
        toastError(res?.message || 'Failed to send invoice.');
      }
    } catch (error) {
      console.error('Send invoice error:', error);
      toastError('An error occurred while sending the invoice.');
    } finally {
      setSendingInvoice(false);
    }
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: any) => {
      const orderPayments = payments.filter((p: any) => p.groupIds?.includes(record.id));
      let isPaid = false;
      if (orderPayments.length > 0) {
        const latestPayment = orderPayments.sort(
          (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0];
        const ps = (latestPayment.status || '').toLowerCase();
        isPaid = ps.includes('paid') || ps.includes('succeeded') || ps.includes('payment-done');
      }

      // Also fallback to group status just in case
      const s = (record.status || '').toLowerCase();
      if (s === 'payment-done') isPaid = true;

      return {
        disabled: isPaid,
        name: record.id,
      };
    },
  };

  const orderColumns = [
    // {
    //   title: 'Order ID',
    //   dataIndex: 'id',
    //   key: 'id',
    //   render: (id: string, record: any) => (
    //     <a onClick={() => onOpenOrder?.(record)} style={{ color: '#1890ff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
    //       {id} <i className="fa fa-file-pdf-o" style={{ color: 'red' }} />
    //     </a>
    //   ),
    // },
    // {
    //   title: 'Order ID',
    //   dataIndex: 'id',
    //   key: 'id',
    //   render: (id: string, record: any) => (
    //     <a
    //       onClick={() => onOpenOrder?.(record)}
    //       style={{
    //         color: '#1890ff',
    //         cursor: 'pointer',
    //         display: 'flex',
    //         alignItems: 'center',
    //         gap: '8px',
    //       }}
    //     >
    //       {id} <i className="fa fa-file-pdf-o" style={{ color: 'red' }} />
    //     </a>
    //   ),
    // },
    {
      title: 'Order ID',
      dataIndex: 'groupCode',
      key: 'groupCode',
      render: (code: string) => (code ? <Tag color="blue">{code}</Tag> : '--'),
    },
    {
      title: 'Group Name',
      dataIndex: 'groupName',
      key: 'groupName',
      render: (name: string) => name || '--',
    },
    // {
    //   title: 'service Name',
    //   dataIndex: 'serviceName',
    //   key: 'serviceName',
    //   render: (name: string) => name || '--',
    // },
    // {
    //   title: 'Order Status',
    //   dataIndex: 'status',
    //   key: 'orderStatus',
    //   render: (status: string) => {
    //     const s = (status || '').toLowerCase();
    //     let color = 'processing';
    //     let text = status;

    //     if (s === 'payment-done') {
    //       color = 'success';
    //       text = 'Payment Done';
    //     } else if (s === 'payment_verification_pending' || s === 'pending-verification') {
    //       color = 'warning';
    //       text = 'Payment Verification Pending';
    //     } else if (s.includes('failed') || s.includes('error')) {
    //       color = 'error';
    //     } else if (s.includes('pending')) {
    //       color = 'warning';
    //     }

    //     return <Tag color={color}>{text}</Tag>;
    //   },
    // },
    {
      title: 'Payment Status',
      key: 'paymentStatus',
      render: (record: any) => {
        const orderPayments = payments.filter((p: any) => p.groupIds?.includes(record.id));
        if (orderPayments.length === 0) return <Tag color="warning">Payment Pending</Tag>;

        const latestPayment = orderPayments.sort(
          (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0];
        const s = (latestPayment.status || '').toLowerCase();

        let color = 'warning';
        let text = 'Payment Pending';
        if (s.includes('paid') || s.includes('succeeded') || s.includes('payment-done')) {
          color = 'success';
          text = 'Payment Done';
        } else if (s === 'payment_verification_pending' || s === 'pending-verification') {
          color = 'warning';
          text = 'Payment Verification Pending';
        } else if (s.includes('failed') || s.includes('error')) {
          color = 'error';
          text = 'Payment Failed';
        }
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Members',
      key: 'memberCount',
      render: (record: any) => {
        const count = record.members?.length ?? record.memberCount ?? record.numberOfMembers ?? 0;
        return Number(count) || 0;
      },
    },
    {
      title: 'Total Fee',
      key: 'totalPrice',
      render: (record: any) => {
        const memberCount =
          record.members?.length ?? record.memberCount ?? record.numberOfMembers ?? 0;
        const customPrice = record.partnerPackage?.customPrice;
        const basePrice = record.partnerPackage?.package?.basePrice;
        const price =
          customPrice !== undefined && customPrice !== null
            ? Number(customPrice)
            : basePrice !== undefined && basePrice !== null
              ? Number(basePrice)
              : 0;
        return `$${(price * Number(memberCount || 0)).toFixed(2)}`;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (date ? new Date(date).toLocaleDateString() : '--'),
    },
  ];

  const paymentColumns = [
    {
      title: 'Payment ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => <Typography.Text copyable>{id}</Typography.Text>,
    },
    {
      title: 'Stripe Intent ID',
      dataIndex: 'stripePaymentIntentId',
      key: 'stripePaymentIntentId',
      render: (intent: string) =>
        intent ? <Typography.Text copyable>{intent}</Typography.Text> : '--',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `$${Number(amount || 0).toFixed(2)}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        let color = 'default';
        if (status === 'payment-done' || status === 'paid' || status === 'succeeded')
          color = 'success';
        else if (status === 'pending') color = 'warning';
        else if (status === 'failed') color = 'error';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => (date ? new Date(date).toLocaleDateString() : '--'),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  let totalPaidAmount = 0;
  let totalPendingAmount = 0;

  (Array.isArray(orders) ? orders : []).forEach((record: any) => {
    const memberCount = record.members?.length ?? record.memberCount ?? record.numberOfMembers ?? 0;
    const customPrice = record.partnerPackage?.customPrice;
    const basePrice = record.partnerPackage?.package?.basePrice;
    const price =
      customPrice !== undefined && customPrice !== null
        ? Number(customPrice)
        : basePrice !== undefined && basePrice !== null
          ? Number(basePrice)
          : 0;
    const amount = price * Number(memberCount || 0);

    const orderPayments = payments.filter((p: any) => p.groupIds?.includes(record.id));
    if (orderPayments.length === 0) {
      totalPendingAmount += amount;
    } else {
      const latestPayment = orderPayments.sort(
        (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0];
      const s = (latestPayment.status || '').toLowerCase();
      if (s.includes('paid') || s.includes('succeeded') || s.includes('payment-done')) {
        totalPaidAmount += amount;
      } else {
        totalPendingAmount += amount;
      }
    }
  });

  const ordersTable = (
    <div
      style={{
        marginBottom: '20px',
        padding: '20px',
        background: '#fff',
        borderRadius: '8px',
        border: '1px solid #f0f0f0',
      }}
    >
      <div
        style={{
          padding: '16px 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <div style={{ display: 'flex', gap: '16px' }}>
          <div
            style={{
              background: '#fffbe6',
              border: '1px solid #ffe58f',
              padding: '8px 16px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span style={{ color: '#d48806', fontWeight: 500 }}>Total Outstanding:</span>
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#faad14' }}>
              ${totalPendingAmount.toFixed(2)}
            </span>
          </div>
          <div
            style={{
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
              padding: '8px 16px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span style={{ color: '#389e0d', fontWeight: 500 }}>Total Paid:</span>
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
              ${totalPaidAmount.toFixed(2)}
            </span>
          </div>
        </div>
        <InvertFillButton
          variant="blue"
          size="md"
          fontWeight="semibold"
          disabled={selectedRowKeys.length === 0 || sendingInvoice}
          onClick={handleSendInvoice}
        >
          <i className="fa fa-envelope"></i>
          {sendingInvoice ? 'Sending Email...' : 'Send Email'}
        </InvertFillButton>
      </div>
      <Table
        rowSelection={rowSelection}
        dataSource={Array.isArray(orders) ? orders : []}
        columns={orderColumns}
        rowKey="id"
        bordered
        pagination={{ pageSize: 10 }}
        scroll={{ x: 'max-content' }}
      />
    </div>
  );

  if (hideAgentInfo) {
    return <div style={{ marginTop: 20, marginBottom: 20 }}>{ordersTable}</div>;
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Agent Information Section */}
      <div style={{ marginBottom: '20px' }}>
        <Accordian title="Agent Information:">
          {agentDetails ? (
            <Card
              style={{
                width: '100%',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                border: 'none',
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                <div style={{ display: 'flex', minWidth: '200px' }}>
                  <div>Partner ID:</div>
                  <div style={{ marginLeft: '10px', fontSize: '15px', fontWeight: '600' }}>
                    {agentDetails?.partnerId || '--'}
                  </div>
                </div>
                <div style={{ display: 'flex', minWidth: '200px' }}>
                  <div>Partner Name:</div>
                  <div style={{ marginLeft: '10px', fontSize: '15px', fontWeight: '600' }}>
                    {agentDetails?.partnerName || '--'}
                  </div>
                </div>
                <div style={{ display: 'flex', minWidth: '200px' }}>
                  <div>Email:</div>
                  <div style={{ marginLeft: '10px', fontSize: '15px', fontWeight: '600' }}>
                    {agentDetails?.email || '--'}
                  </div>
                </div>
                <div style={{ display: 'flex', minWidth: '200px' }}>
                  <div>Company Name:</div>
                  <div style={{ marginLeft: '10px', fontSize: '15px', fontWeight: '600' }}>
                    {agentDetails?.companyName || '--'}
                  </div>
                </div>
                <div style={{ display: 'flex', minWidth: '200px' }}>
                  <div>Phone:</div>
                  <div style={{ marginLeft: '10px', fontSize: '15px', fontWeight: '600' }}>
                    {agentDetails?.contactNumber || '--'}
                  </div>
                </div>
                <div style={{ display: 'flex', minWidth: '200px' }}>
                  <div>Country:</div>
                  <div style={{ marginLeft: '10px', fontSize: '15px', fontWeight: '600' }}>
                    {agentDetails?.originCountry || '--'}
                  </div>
                </div>
                <div style={{ display: 'flex', minWidth: '200px' }}>
                  <div>Status:</div>
                  <div style={{ marginLeft: '10px', fontSize: '15px', fontWeight: '600' }}>
                    {agentDetails?.status || '--'}
                  </div>
                </div>
                <div style={{ display: 'flex', minWidth: '200px' }}>
                  <div>Total Orders:</div>
                  <div style={{ marginLeft: '10px', fontSize: '15px', fontWeight: '600' }}>
                    {agentDetails?.totalOrders || 0}
                  </div>
                </div>
              </div>
            </Card>
          ) : (
            <p>Agent details not found.</p>
          )}
        </Accordian>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <Accordian title="Agent Orders:">
          <Card
            style={{
              width: '100%',
              borderRadius: '8px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              padding: '0',
              border: 'none',
            }}
            bodyStyle={{ padding: '0' }}
          >
            <div
              style={{
                padding: '14px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <div style={{ display: 'flex', gap: '14px' }}>
                <div
                  style={{
                    background: '#fffbe6',
                    border: '1px solid #ffe58f',
                    padding: '8px 14px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <span style={{ color: '#d48806', fontWeight: 500 }}>Total Outstanding:</span>
                  <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#faad14' }}>
                    ${totalPendingAmount.toFixed(2)}
                  </span>
                </div>
                <div
                  style={{
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    padding: '8px 14px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <span style={{ color: '#389e0d', fontWeight: 500 }}>Total Paid:</span>
                  <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
                    ${totalPaidAmount.toFixed(2)}
                  </span>
                </div>
              </div>
              <Button
                type="primary"
                icon={<i className="fa fa-envelope" style={{ marginRight: 8 }}></i>}
                disabled={selectedRowKeys.length === 0}
                loading={sendingInvoice}
                onClick={handleSendInvoice}
              >
                Send Invoice
              </Button>
            </div>
            <Table
              rowSelection={rowSelection}
              dataSource={Array.isArray(orders) ? orders : []}
              columns={orderColumns}
              rowKey="id"
              pagination={{ pageSize: 5 }}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </Accordian>
      </div>
    </div>
  );
};

export default AgentPaymentLedgerDetails;
